import { debounce } from 'lodash';
import { ChangeEvent, Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageFileParams } from '@/api/image';
import { TabClickProps } from '@/components/atom/Tab';
import { BottomSheetTabsListItemProps } from '@/components/molecules/BottomSheet/BottomSheetTabs/BottomSheetTabsList';
import useSetAuthOnScreenFocus from '@/hooks/auth/useSetAuthOnScreenFocus';
import usePolicyAgreementBottomSheetViewModel, {
  PolicyAgreementBottomSheetViewModel,
} from '@/hooks/qna/mobile/Write/usePolicyAgreementBottomSheetViewModel';
import { useAddQuestionWithAiAnswerMutation } from '@/hooks/qna/mobile/qna.gql.impl';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import useGetCommunityUser from '@/hooks/useGetCommunityUser';
import useModal from '@/hooks/useModal';
import useNeverSeeAgain from '@/hooks/useNeverSeeAgain';
import useToast from '@/hooks/useToast';
import useUploadImage from '@/hooks/useUploadImage';
import { tokenState } from '@/recoil/atoms/common/auth';
import { neverSeeAgainState, neverSeeAgainType } from '@/recoil/atoms/common/neverSeeAgain';
import { userState } from '@/recoil/atoms/common/user';
import { realTimeAnswerState } from '@/recoil/atoms/qna';
import { AiAnswerStatus, QuestionStatus } from '@/types/bff/gql-schema';
import { FileWithBlobUrl, Nullable, ROUTES } from '@/types/common';
import { CommunityUser } from '@/types/community';
import { GraphqlError } from '@/types/error';
import { UserCoordinate } from '@/types/hospital';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { AddQuestionResponse, ImagePath, QnaCategoryGroup, QnaCategoryType } from '@/types/qna';
import { getBffErrorMessage } from '@/utils/error';
import { convertHeic, onCompressImage, setImageUrl } from '@/utils/image';
import { communityLogger, mobileLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, historyBack, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';

import useGetMyUncheckedAiAnswer from './mobile/Home/useGetMyUncheckedAiAnswer';
import useQnaWriteBottomSheetGroup, { UseQnaWriteBottomSheetGroup } from './mobile/Write/useQnaWriteBottomSheetGroup';
import useGetQnaCategoryGroup from './useGetQnaCategoryGroup';

interface QnaWriteSubmitProps {
  coordinate?: UserCoordinate;
  isAiAnswer?: boolean;
}

export interface QnaWriteMobileViewModel {
  question: string;
  setQuestion: Dispatch<string>;
  onClickCheckPregnant: () => void;
  pregnant: boolean;
  selectedCategoriesBottomSheetTabItem: Nullable<BottomSheetTabsListItemProps>;
  selectedTags: string[];
  setSelectedTags: Dispatch<SetStateAction<string[]>>;
  showCategoriesBottomSheet: boolean;
  toggleCategoriesBottomSheet: (value: boolean) => void;
  isUploadingImage: boolean;
  isSubmitLoading: boolean;
  userProfile?: CommunityUser;
  onClickCategoriesBottomSheetTabListItem: (item: BottomSheetTabsListItemProps) => void;
  onCloseGuide: (skip?: boolean) => void;
  neverSeeAgainGuide: boolean;
  neverSeeAgain: neverSeeAgainType;
  categoryGroup?: QnaCategoryGroup;
  selectedTab: QnaCategoryType;
  onClickCategoriesBottomSheetTab: (tab: TabClickProps<QnaCategoryType>) => void;
  getSelectedTabItemList: () => BottomSheetTabsListItemProps[];
  questionImages: FileWithBlobUrl[];
  imageInputRef: RefObject<HTMLInputElement>;
  onDeleteImage: (idx: number) => void;
  onUploadImage: () => void;
  onHandleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onClickApproveGeolocation: (isAiAnswer?: boolean) => void;
  onClickNearDoctorAlertSkip: () => void;
  checkEnableSubmit: (e: React.MouseEvent) => void;
  isSubmitButtonDisabled: boolean;
  qnaWriteBottomSheetGroup: UseQnaWriteBottomSheetGroup;
  requestExpertAnswer: () => void;
  requestAiAnswer: () => void;
  policyAgreementBottomSheetViewModel: PolicyAgreementBottomSheetViewModel;
}

const useQnaWriteMobileViewModel = (): QnaWriteMobileViewModel => {
  useSetAuthOnScreenFocus();
  useNeverSeeAgain();
  const {setShowAuthModal} = useAuthModal();
  const token = useRecoilValue(tokenState);
  const { pageName, moveToPage } = useCustomRouter();
  const { showToast } = useToast();
  const { showModal, closeModal } = useModal();
  const setRealTimeAnswerState = useSetRecoilState(realTimeAnswerState);
  const qnaWriteBottomSheetGroup = useQnaWriteBottomSheetGroup();
  const unCheckedAiAnswer = useGetMyUncheckedAiAnswer();
  const policyAgreementBottomSheetViewModel = usePolicyAgreementBottomSheetViewModel();
  const [question, setQuestion] = useState<string>('');
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [pregnant, setPregnant] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<Array<string>>([]);
  const [attachments, setAttachments] = useState<Array<ImagePath>>([]);
  const [showCategoriesBottomSheet, setShowCategoriesBottomSheet] = useState<boolean>(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const { userProfile } = useGetCommunityUser();
  const [neverSeeAgain, setNeverSeeAgain] = useRecoilState(neverSeeAgainState);
  const [neverSeeAgainGuide, setNeverSeeAgainGuide] = useState<boolean>(neverSeeAgain.qnaWriteGuide);
  const user = useRecoilValue(userState);

  const [selectedTab, setSelectedTab] = useState<QnaCategoryType>(QnaCategoryType.TREATMENT);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [questionImages, setQuestionImages] = useState<Array<FileWithBlobUrl>>([]);

  const { categoryGroup } = useGetQnaCategoryGroup();

  // 이미지 등록
  const { mutateAsync: uploadImageMutateAsync } = useUploadImage();

  // 질문 작성
  const { mutate: addQuestionWithAiAnswerMutate } = useAddQuestionWithAiAnswerMutation();

  const [selectedCategoriesBottomSheetTabItem, setSelectedCategoriesBottomSheetTabItem] =
    useState<Nullable<BottomSheetTabsListItemProps>>();

  const isSubmitButtonDisabled = useMemo(() => {
    return (
      isSubmitLoading || isUploadingImage || userProfile?.isBanned || qnaWriteBottomSheetGroup.isGeolocationLoading
    );
  }, [isSubmitLoading, isUploadingImage, userProfile?.isBanned, qnaWriteBottomSheetGroup.isGeolocationLoading]);

  const onSubmitSuccess = (response: AddQuestionResponse) => {
    if (!isWebView()) {
      setRealTimeAnswerState(true);
    }

    mobileLogger({
      logger: {
        eventName: 'quick_answer_write_submit_click',
        pageName: pageName,
      },
    });

    const {
      addQuestionV2: { aiAnswerAvailable, questionPid },
    } = response;
    if (aiAnswerAvailable) {
      moveToPage({
        webLink: `${ROUTES.QNA.AI.path}/${questionPid}`,
        mobileScreenName: ROUTES.QNA.AI.appScreenName,
        params: { questionPid: questionPid },
        type: NATIVE_POST_MESSAGE_TYPE.REPLACE_TO_PAGE,
      });

      return;
    }

    moveToPage({
      webLink: `${ROUTES.QNA.AITITLE.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.AITITLE.appScreenName,
      params: { questionPid: questionPid },
      type: NATIVE_POST_MESSAGE_TYPE.REPLACE_TO_PAGE,
    });
  };

  const onSubmitError = (error: GraphqlError) => {
    return showToast({
      message: getBffErrorMessage(error),
    });
  };

  const onClickCheckPregnant = () => {
    setPregnant((prev) => !prev);
  };

  // 카테고리 Bottom Sheet Show/Hide Toggle
  const toggleCategoriesBottomSheet = (value = true) => {
    setShowCategoriesBottomSheet(value);
  };

  // 카테고리 Bottom Sheet Tab Click
  const onClickCategoriesBottomSheetTab = (tab: TabClickProps<QnaCategoryType>) => {
    setSelectedTab(tab.value);
  };

  const getSelectedTabItemList = () => {
    if (!categoryGroup) return [] as BottomSheetTabsListItemProps[];
    return categoryGroup.tabsList.filter((tab) => tab.key === selectedTab)[0].categories;
  };

  // Bottom Sheet Tab List Item Click Handler
  const onClickCategoriesBottomSheetTabListItem = (item: BottomSheetTabsListItemProps) => {
    setSelectedCategoriesBottomSheetTabItem(item);
    toggleCategoriesBottomSheet(false);
  };

  const onCloseGuide = (skip?: boolean) => {
    mobileLogger({
      logger: {
        eventName: skip ? 'qna_onboarding_skip_click' : 'qna_onboarding_complete_click',
        pageName: pageName,
      },
    });
    if (!isWebView()) {
      setNeverSeeAgain({
        ...neverSeeAgain,
        qnaWriteGuide: true,
      });
    } else {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.SET_NEVER_SEE_AGAIN, {
        state: 'qnaWriteGuide',
      });
    }
    setNeverSeeAgainGuide(true);
  };

  const onDeleteImage = (idx: number) => {
    setQuestionImages(() => {
      const images = questionImages.slice();
      images.splice(idx, 1);
      return images;
    });

    setAttachments(() => {
      const newAttachments = attachments.slice();
      newAttachments.splice(idx, 1);
      return newAttachments;
    });
  };

  const onUploadImage = () => {
    if (!token) {
      // 로그인 정보가 없는 경우
      setShowAuthModal(true);
    } else {
      imageInputRef.current?.click();
    }
  };

  const uploadImageToS3 = async (files: Array<ImageFileParams>) => {
    const uploadedImages: Array<FileWithBlobUrl> = [];
    const uploadedAttachments: Array<ImagePath> = [];
    const uploadFiles = files.map(async (file: ImageFileParams) => {
      const { success, imagePath } = await uploadImageMutateAsync({
        fileExtension: file.origin.type.split('/')[1],
        imageFile: { origin: file.origin, thumbnail: file.thumbnail },
      });
      if (success) {
        const image = await setImageUrl(file.thumbnail);
        uploadedImages.push(image);
        uploadedAttachments.push({
          originalPath: imagePath.originalPath,
          thumbnailPath: imagePath.thumbnailPath,
        });
      }
    });
    await Promise.all(uploadFiles);

    setQuestionImages([...questionImages, ...uploadedImages]);
    setAttachments([...attachments, ...uploadedAttachments]);
    setIsUploadingImage(false);
  };

  const onHandleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const attachedFile = Array.from(e.target.files);
      const enabledImageCount = 4 - questionImages.length;
      if (enabledImageCount > 0) {
        setIsUploadingImage(true);
        const filteredImages = attachedFile.slice(0, enabledImageCount).filter((image) => {
          const isImage = image.type.includes('image');
          if (!isImage) {
            return showModal({
              type: MODAL_TYPE.ALERT,
              props: {
                title: '사진 파일을 올려주세요',
                message: 'jpg, jpeg, png 확장자만 올릴 수 있어요.',
                buttons: [
                  {
                    type: MODAL_BUTTON_TYPE.BOLD,
                    label: '확인',
                    onClick: () => {
                      closeModal();
                    },
                  },
                ],
              },
            });
          }
          return isImage;
        });

        const convertedImages = await Promise.all(
          filteredImages.map(async (image) => {
            if (image.type.includes('heic')) {
              return await convertHeic(image);
            }
            return image;
          }),
        );

        const images = await Promise.all(
          convertedImages.map(async (image) => {
            return {
              origin: await onCompressImage(image, {
                maxSizeMB: 5,
              }),
              thumbnail: await onCompressImage(image, {
                maxSizeMB: 0.2,
                maxWidthOrHeight: 300,
              }),
            };
          }),
        );
        await uploadImageToS3(images);
      }
    }
  };

  const checkEnableSubmit = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!token) {
      setShowAuthModal(true);
      return;
    }

    if (isWebView()) {
      if (!user.isCertified) {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_CERTIFICATION_SCREEN, {
          params: {
            base: 'qna',
          },
        });
        return false;
      }
    }

    if (question.length <= 4) {
      showToast({ message: '질문을 최소 5자 이상 입력해주세요.' });
      return false;
    }
    if (!selectedCategoriesBottomSheetTabItem) {
      showToast({ message: '카테고리를 선택해주세요.' });
      return false;
    }

    if (
      policyAgreementBottomSheetViewModel.hasAlreadyAgreedToAll() ||
      policyAgreementBottomSheetViewModel.hasAgreedToMinimum()
    ) {
      requestAiAnswer();
    } else {
      policyAgreementBottomSheetViewModel.openBottomSheet();
    }
  };

  const requestExpertAnswer = debounce(
    () => {
      communityLogger({
        eventName: 'click_expert_answer_bottom_sheet',
        pageName: pageName,
        payload: {
          type: qnaWriteBottomSheetGroup.activeBottomSheetType ?? '',
        },
      });

      if (qnaWriteBottomSheetGroup.isApprovedByLocationCategory || qnaWriteBottomSheetGroup.isCheckedAgreement) {
        onClickApproveGeolocation(false);
      } else {
        onSubmit({ isAiAnswer: false });
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  const requestAiAnswer = () => {
    communityLogger({
      eventName: 'click_ai_answer_bottom_sheet',
      pageName: pageName,
      payload: {
        type: qnaWriteBottomSheetGroup.activeBottomSheetType ?? '',
      },
    });

    onSubmit({ isAiAnswer: true });
  };

  // 질문 submit
  const onSubmit = async ({ coordinate, isAiAnswer }: QnaWriteSubmitProps) => {
    if (!selectedCategoriesBottomSheetTabItem) return;
    setIsSubmitLoading(true);
    addQuestionWithAiAnswerMutate(
      {
        request: {
          content: question,
          pregnant,
          categoryId: selectedCategoriesBottomSheetTabItem.value as string,
          tagNames: selectedTags,
          aiAnswerAvailable: isAiAnswer,
          location: coordinate ?? null,
          attachments,
        },
      },
      {
        onSuccess: onSubmitSuccess,
        onError: onSubmitError,
      },
    );
  };

  const onClickApproveGeolocation = (isAiAnswer = true) => {
    communityLogger({
      eventName: 'click_near_doctor_alert_approve',
      pageName: pageName,
      payload: {
        type: qnaWriteBottomSheetGroup.activeBottomSheetType ?? '',
      },
    });
    qnaWriteBottomSheetGroup.requestGeolocation({
      onSuccess: (coordinate?: UserCoordinate) => {
        qnaWriteBottomSheetGroup.setApproveAgreement();
        onSubmit({ coordinate, isAiAnswer });
      },
    });
  };

  const onClickNearDoctorAlertSkip = () => {
    communityLogger({
      eventName: 'click_near_doctor_alert_skip',
      pageName: pageName,
      payload: {
        type: qnaWriteBottomSheetGroup.activeBottomSheetType ?? '',
      },
    });
    qnaWriteBottomSheetGroup.onChangeBottomSheetType();
    onSubmit({
      isAiAnswer: selectedCategoriesBottomSheetTabItem?.aiAnswerAvailable,
    });
  };

  const onClickAiAnswerDetail = (questionPid: string) => {
    moveToPage({
      webLink: `${ROUTES.QNA.AI.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.AI.appScreenName,
      params: {
        questionPid,
      },
    });
  };

  useEffect(() => {
    setSelectedTags([]);
  }, [selectedCategoriesBottomSheetTabItem]);

  useEffect(() => {
    if (isWebView()) {
      if (question || selectedCategoriesBottomSheetTabItem || attachments.length) {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.SET_PREVENT_BACK, {
          preventBack: true,
        });
      } else {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.SET_PREVENT_BACK, {
          preventBack: false,
        });
      }
    }
  }, [question, selectedCategoriesBottomSheetTabItem, attachments]);

  useEffect(() => {
    if (userProfile?.isBanned) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '서비스 이용 제한',
          message: `실시간 상담 운영 정책 위반으로 활동이 제한 되었습니다.`,
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.DEFAULT,
              label: '확인',
              onClick: () => {
                closeModal();
                historyBack();
              },
            },
          ],
        },
      });
    }
  }, [userProfile]);

  useEffect(() => {
    if (
      unCheckedAiAnswer &&
      (unCheckedAiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationRequested ||
        unCheckedAiAnswer.aiAnswerStatus === AiAnswerStatus.Generating ||
        unCheckedAiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationComplete ||
        unCheckedAiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationFailed) &&
      unCheckedAiAnswer.status !== QuestionStatus.PaymentFailed
    )
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '기존 답변을 먼저 확인해 주세요',
          message: '고민이 해결됐는지 의료진 상담이 필요한지 알려주신 뒤 다음 질문을 작성할 수 있어요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.DEFAULT,
              label: '확인',
              onClick: () => {
                closeModal();
                historyBack();
              },
            },
            {
              type: MODAL_BUTTON_TYPE.PRIMARY,
              label: '답변 보기',
              onClick: () => {
                closeModal();
                onClickAiAnswerDetail(unCheckedAiAnswer.questionPid);
              },
            },
          ],
        },
      });
  }, [unCheckedAiAnswer?.aiAnswerStatus]);

  return {
    question,
    setQuestion,
    onClickCheckPregnant,
    pregnant,
    selectedCategoriesBottomSheetTabItem,
    selectedTags,
    setSelectedTags,
    showCategoriesBottomSheet,
    toggleCategoriesBottomSheet,
    isUploadingImage,
    isSubmitLoading,
    userProfile,
    onClickCategoriesBottomSheetTabListItem,
    onCloseGuide,
    neverSeeAgainGuide,
    neverSeeAgain,
    categoryGroup,
    selectedTab,
    onClickCategoriesBottomSheetTab,
    getSelectedTabItemList,
    questionImages,
    imageInputRef,
    onDeleteImage,
    onUploadImage,
    onHandleChange,
    onClickApproveGeolocation,
    onClickNearDoctorAlertSkip,
    checkEnableSubmit,
    isSubmitButtonDisabled,
    qnaWriteBottomSheetGroup,
    requestExpertAnswer,
    requestAiAnswer,
    policyAgreementBottomSheetViewModel: policyAgreementBottomSheetViewModel,
  };
};
export default useQnaWriteMobileViewModel;
